import { AppointmentStatus, IAppointment } from '.';
import { AssetVisitEventType, IAssetVisitEvent } from './asset-visit-event';
import { ISpotAssignment } from './yard';

export enum AssetVisitType {
  LiveUnload = 'LiveUnload',
  LiveLoad = 'LiveLoad',
  Live = 'Live',
  Drop = 'Drop',
  PickUp = 'PickUp',
  DropHook = 'DropHook',
  Unknown = 'Unknown'
}

export enum AssetVisitDirection {
  Inbound = 'Inbound',
  Outbound = 'Outbound',
  InboundAndOutbound = 'InboundAndOutbound'
}

export enum AssetVisitYardEventType {
  Arrived = 'Arrived',
  Docked = 'Docked',
  Parked = 'Parked',
  Unassigned = 'Unassigned',
  Dropped = 'Dropped',
  Hooked = 'Hooked',
  AppointmentLinked = 'Appointment Linked',
  AppointmentUnlinked = 'Appointment Unlinked',
  StatusUpdated = 'Status Updated'
}

export interface IAssetVisit {
  id: string;
  orgId: string;
  appointmentId?: string;
  secondaryAppointmentId?: string;
  appointment?: IAppointment;
  secondaryAppointment?: IAppointment;
  isPlanned: boolean;
  assetVisitEvents: IAssetVisitEvent[];
  spotAssignments: ISpotAssignment[];
  visitType: AssetVisitType;
  phone: string;
  companyHint?: string;
  driverNotes?: string;
  driverAppointmentIdentifier?: string;
}

export interface IAssetVisitYardEvent {
  createDateTime: Date;
  createdBy: string;
  details?: Record<string, any>;
  eventType: AssetVisitYardEventType;
}

export function shouldCreateAssetContainerFromAssetVisit(visitType: AssetVisitType): boolean {
  return [AssetVisitType.Drop, AssetVisitType.Live, AssetVisitType.DropHook].includes(visitType);
}

export function isAssetVisitPlanned(assetVisit: IAssetVisit) {
  return (assetVisit.isPlanned && !isAssetVisitAppointmentFinished(assetVisit)) ?? false;
}

export function isAssetVisitUnplannedAndUnresolved(assetVisit: IAssetVisit) {
  return Boolean(
    !assetVisit.isPlanned &&
      !assetVisit.appointmentId &&
      !isAssetVisitCancelled(assetVisit) &&
      !isAssetVisitDeparted(assetVisit)
  );
}

export function isAssetVisitUnplannedAndResolved(assetVisit: IAssetVisit): boolean {
  return Boolean(
    !assetVisit.isPlanned &&
      (assetVisit.appointmentId ||
        isAssetVisitCancelled(assetVisit) ||
        isAssetVisitDeparted(assetVisit))
  );
}

export function isAssetVisitAppointmentFinished(assetVisit: IAssetVisit): boolean {
  return Boolean(
    [
      AppointmentStatus.InProgress,
      AppointmentStatus.Completed,
      AppointmentStatus.Cancelled,
      AppointmentStatus.NoShow
    ].includes(assetVisit.appointment?.status as AppointmentStatus)
  );
}

export function isAssetVisitDeparted(assetVisit: IAssetVisit): boolean {
  return Boolean(
    assetVisit.assetVisitEvents?.some(event => event.eventType === AssetVisitEventType.Departed)
  );
}

export function isAssetVisitDocked(assetVisit: IAssetVisit): boolean {
  return Boolean(
    assetVisit.assetVisitEvents?.some(event => event.eventType === AssetVisitEventType.Docked)
  );
}

export function isAssetVisitCancelled(assetVisit: IAssetVisit): boolean {
  return Boolean(
    assetVisit.assetVisitEvents?.some(event => event.eventType === AssetVisitEventType.Canceled)
  );
}

export function isAssetVisitAssigned(assetVisit: IAssetVisit): boolean {
  return Boolean(assetVisit?.spotAssignments?.length > 0);
}

export function getPlannedAssetVisits(assetVisits: IAssetVisit[]): IAssetVisit[] {
  if (!Array.isArray(assetVisits)) {
    return [];
  }
  return assetVisits.filter(isAssetVisitPlanned);
}

export function getUnplannedAndUnresolvedAssetVisits(assetVisits: IAssetVisit[]): IAssetVisit[] {
  if (!Array.isArray(assetVisits)) {
    return [];
  }
  return assetVisits.filter(isAssetVisitUnplannedAndUnresolved);
}

export function getResolvedAssetVisits(assetVisits: IAssetVisit[]): IAssetVisit[] {
  if (!Array.isArray(assetVisits)) {
    return [];
  }
  return assetVisits.filter(
    assetVisit =>
      isAssetVisitUnplannedAndResolved(assetVisit) || isAssetVisitAppointmentFinished(assetVisit)
  );
}

export function getWaitingAssetVisits(assetVisits: IAssetVisit[]): IAssetVisit[] {
  if (!Array.isArray(assetVisits)) {
    return [];
  }

  return (
    assetVisits?.filter(
      assetVisit =>
        !isAssetVisitCancelled(assetVisit) &&
        !isAssetVisitDeparted(assetVisit) &&
        !isAssetVisitAssigned(assetVisit)
    ) || []
  );
}
